import { isEqual } from "lodash";
import { createContext, Dispatch, useContext, useEffect, useReducer } from "react";
import { arsTableReducer } from "./arsTableReducer";
import {
  ARST_CHANGE_SENT,
  ARST_LOAD_DATA,
  ARST_SET_TLF_VIEW_MODE,
  ARST_UPDATE_SCAFFOLD,
  ArsTableAction,
  ArsTableBody,
  ArsTableRow,
  ArsTableSettings,
  ArsTableState,
} from "./interface";

interface ArsTableContextProps<T> {
  state: ArsTableState<T>;
  dispatch: Dispatch<ArsTableAction<T>>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ArsTableContext = createContext<ArsTableContextProps<any> | null>(null);

interface ArsTableContextProviderProps<T> {
  children: React.ReactNode;
  bodyData: ArsTableBody<T>;
  calculatedTableRows: ArsTableRow<T>[] | null;
  settings: ArsTableSettings<T>;
  updateBodyData: (ret: ArsTableBody<T>) => void;
}

/**
 * Provider that supplies listing state and dispatch to child components.
 *
 * @param props - Component props
 * @param props.children - Child nodes wrapped by the provider
 * @param props.bodyData - Body data to render
 * @param props.calculatedTableRows - Calculated table rows to render
 * @param props.settings - Table settings including view mode and other options
 * @param props.updateBodyData - Function to partially update the body data
 * @returns ArsTableContextProvider component
 */
export const ArsTableContextProvider = <T,>({
  children,
  bodyData,
  calculatedTableRows,
  settings,
  updateBodyData,
}: ArsTableContextProviderProps<T>) => {
  const [state, dispatch] = useReducer(arsTableReducer, {
    bodyData,
    scaffoldModel: null,
    calculatedTableRows,
    settings,
    changed: false,
  });

  useEffect(() => {
    const eq = isEqual(state.bodyData, bodyData);
    // Send changes up
    if (!eq && state.bodyData.id === bodyData.id && state.changed) {
      updateBodyData(state.bodyData);
      dispatch({ operation: ARST_CHANGE_SENT });
    }
    // Accept new input if it differs from current state and wasn't just sent
    else if (state.bodyData.id !== bodyData.id || !eq) {
      dispatch({ operation: ARST_LOAD_DATA, bodyData });
    }
  }, [bodyData, state.changed, state.bodyData, updateBodyData]);

  useEffect(() => {
    if (!isEqual(state.settings.tlfViewMode, settings.tlfViewMode)) {
      dispatch({ operation: ARST_SET_TLF_VIEW_MODE, tlfViewMode: settings.tlfViewMode });
    }
  }, [settings.tlfViewMode, state.settings.tlfViewMode]);

  useEffect(() => {
    const eq = isEqual(state.calculatedTableRows, calculatedTableRows);
    if (!eq && calculatedTableRows) {
      dispatch({ operation: ARST_UPDATE_SCAFFOLD, calculatedTableRows });
    }
  }, [calculatedTableRows, state.calculatedTableRows]);

  return (
    <ArsTableContext.Provider value={{ state, dispatch }}>{children}</ArsTableContext.Provider>
  );
};

ArsTableContextProvider.displayName = "ArsTableContextProvider";

/**
 * Custom hook to access the ArsTable context.
 *
 * @returns The table context object
 */
export const useArsTable = <T = string,>() => {
  const context = useContext(ArsTableContext);
  return !context
    ? {
        state: null,
        dispatch: () => console.error("WARNING: Dispatch not initiated"),
      }
    : (context as ArsTableContextProps<T>);
};
